import React, {useState} from "react";
import DocumentLoader from "features/components/document-loader";
import {__} from "services/i18n";
import "./index.scss";
import {bool, func} from "prop-types";
import {
    PhoneTextFieldValidator,
    ProfileValidationsErrors, ProfileValidator
} from "services/utils/validations";
import Title from "features/components/title";
import {TextField} from "features/components/text-field";
import Select from "features/components/select";
import {GENDERS, LANGUAGES, SERVER_DATE_FORMAT} from "constants.js";
import DatePickerComponent from "features/components/date-picker";
import TextArea from "features/components/text-area";
import {Icon} from "features/components/icon";
import Text from "features/components/text";
import ChipSelector from "features/components/chip-selector";
import {get} from "lodash";
import {connect} from "react-redux";
import {downloadStaticData} from "store/reducer/static/reducer";
import {staticDataInterests, staticDataOsaOffices, staticDataSkills} from "store/reducer/static/selectors";
import Errors from "features/components/errors";

/* istanbul ignore next */
const mapStateToProps = state => ({
    skillsData: staticDataSkills(state),
    interestsData: staticDataInterests(state),
    osaOffices: staticDataOsaOffices(state)
});
/* istanbul ignore next */
const mapDispatchToProps = {
    downloadStaticData
};


export const ProfileEditForm = (props) => {
    const {onChange, disabled, downloadStaticData, skillsData, interestsData, osaOffices} = props;
    const [fileVal, setFile] = useState(props.image);
    const [firstNameVal, setFirstName] = useState(props.firstName);
    const [lastNameVal, setLastName] = useState(props.lastName);
    const [birthDateVal, setBirthDate] = useState(props.birthDate);
    const [genderVal, setGender] = useState(props.gender);
    const [officeVal, setOffice] = useState(props.office);
    const [userBioVal, setUserBio] = useState(props.userBio);
    const [phoneVal, setPhone] = useState(props.phone);
    const [languagesVal, setLanguages] = useState(props.languages);
    const [linkedinVal, setLinkedin] = useState(props.linkedin);
    const [twitterVal, setTwitter] = useState(props.twitter);
    const [websiteVal, setWebsite] = useState(props.website);
    const [skillsVal, setSkills] = useState(props.skills);
    const [interestsVal, setInterests] = useState(props.interests);

    const [firstLinkDescVal, setFirstLinkDesc] = useState(get(props.links, '0.desc', ""))
    const [firstLinkVal, setFirstLink] = useState(get(props.links, '0.link', ""))
    const [secondLinkDescVal, setSecondLinkDesc] = useState(get(props.links, '1.desc', ""))
    const [secondLinkVal, setSecondLink] = useState(get(props.links, '1.link', ""))
    const [thirdLinkDescVal, setThirdLinkDesc] = useState(get(props.links, '2.desc', ""))
    const [thirdLinkVal, setThirdLink] = useState(get(props.links, '2.link', ""))

    const [formErrors, setFormErrors] = useState([]);

    React.useEffect(() => {
        downloadStaticData()
    }, [downloadStaticData]);

    React.useEffect(() => {
        const data = {
            image: fileVal,
            firstName: firstNameVal,
            lastName: lastNameVal,
            birthDate: birthDateVal,
            gender: genderVal,
            office: officeVal,
            userBio: userBioVal,
            phone: phoneVal,
            languages: languagesVal,
            linkedin: linkedinVal,
            twitter: twitterVal,
            website: websiteVal,
            skills: skillsVal,
            interests: interestsVal,
            links: [
                {desc: firstLinkDescVal, link: firstLinkVal},
                {desc: secondLinkDescVal, link: secondLinkVal},
                {desc: thirdLinkDescVal, link: thirdLinkVal}
            ]
        };
        onChange({
            ...data,
            valid: ProfileValidator(data)
        });
        setFormErrors(ProfileValidationsErrors(data))
    }, [onChange, fileVal, firstNameVal, lastNameVal, birthDateVal, genderVal, officeVal,
        userBioVal, phoneVal, languagesVal, linkedinVal, twitterVal, websiteVal, skillsVal, interestsVal,
        firstLinkDescVal, firstLinkVal, secondLinkVal, secondLinkDescVal, thirdLinkVal, thirdLinkDescVal, setFormErrors]);

    return <div className="profile-edit-form">
        <DocumentLoader
            buttonTitle={__("buttons.upload-image")}
            text={__("forms.profile-edit.image-text")}
            vertical={true}
            accept={["image/*"]}
            onChange={setFile}
            value={fileVal}
            disabled={!!disabled}
        />
        <div className={"image-pref"}><Text size={14} align={"center"}>{__("forms.profile-edit.image-pref")}</Text></div>
        <div>
            <Title size={18}>{__("forms.profile-edit.base-info.title")}</Title>
            <div className="form-field">
                <TextField
                    type={"text"}
                    value={firstNameVal}
                    onChange={setFirstName}
                    placeholder={__("text-fields.first-name.placeholder")}
                    disabled={!!disabled}
                />
                <TextField
                    type={"text"}
                    value={lastNameVal}
                    onChange={setLastName}
                    placeholder={__("text-fields.last-name.placeholder")}
                    disabled={!!disabled}
                />
            </div>
            <div className="form-field triple">
                <DatePickerComponent
                    type={"text"}
                    value={birthDateVal}
                    onChange={setBirthDate}
                    dateFormat={SERVER_DATE_FORMAT}
                    placeholder={__("text-fields.birth-date.placeholder")}
                    disabled={!!disabled}
                />
                <Select
                    value={genderVal}
                    values={GENDERS}
                    onChange={setGender}
                    placeholder={__("text-fields.gender.placeholder")}
                    disabled={!!disabled}
                />
                <Select
                    value={officeVal}
                    values={osaOffices}
                    onChange={setOffice}
                    placeholder={__("text-fields.osa-office.placeholder")}
                    disabled={!!disabled}
                />
            </div>
        </div>
        <div className="bio">
            <Title size={18}>{__("forms.profile-edit.description.title")}</Title>
            <TextArea value={userBioVal} disabled={!!disabled} onChange={setUserBio} placeholder={__("text-fields.userbio.placeholder")} />
            <div className="form-field langs">
                <Select
                    value={languagesVal}
                    values={LANGUAGES}
                    onChange={setLanguages}
                    multiple={true}
                    placeholder={__("text-fields.languages.placeholder")}
                    disabled={!!disabled}
                />
                <TextField
                    type={"text"}
                    value={phoneVal}
                    onChange={setPhone}
                    placeholder={__("text-fields.phone.placeholder")}
                    validationFunc={PhoneTextFieldValidator}
                    disabled={!!disabled}
                />
            </div>
            <TextField
                type={"text"}
                value={linkedinVal}
                onChange={setLinkedin}
                icon={"linkedin"}
                placeholder={__("text-fields.linkedin.placeholder")}
                disabled={!!disabled}
            />
            <TextField
                type={"text"}
                value={twitterVal}
                onChange={setTwitter}
                icon={"twitter"}
                placeholder={__("text-fields.twitter.placeholder")}
                disabled={!!disabled}
            />
            <TextField
                type={"text"}
                value={websiteVal}
                onChange={setWebsite}
                icon={"web"}
                placeholder={__("text-fields.website.placeholder")}
                disabled={!!disabled}
            />
        </div>
        <div>
            <Title size={18}>{__("forms.profile-edit.skills.title")}</Title>
            <div className="row skill-title">
                <Icon>web</Icon>
                <Text size={16}>{__("forms.profile-edit.skills.skill.text")}</Text>
            </div>
            <ChipSelector
                placeholder={__("forms.profile-edit.skills.skill.placeholder")}
                data={skillsData}
                enableAddSearched={true}
                value={skillsVal}
                onChange={setSkills}
                maxItems={10}
                disabled={!!disabled}
            />
            <div className="row skill-title">
                <Icon>lock</Icon>
                <Text size={16}>{__("forms.profile-edit.skills.interest.text")}</Text>
            </div>
            <ChipSelector
                placeholder={__("forms.profile-edit.skills.interest.placeholder")}
                data={interestsData}
                enableAddSearched={false}
                value={interestsVal}
                onChange={setInterests}
                maxItems={10}
                disabled={!!disabled}
            />
        </div>
        <div className={"other"}>
            <Title size={18}>{__("forms.profile-edit.other.title")}</Title>
            <Text size={16}>{__("forms.profile-edit.other.text")}</Text>
            <div className={"form-field"}>
                <TextField
                    type={"text"}
                    value={firstLinkDescVal}
                    onChange={setFirstLinkDesc}
                    placeholder={__("text-fields.link-title.placeholder")}
                    disabled={!!disabled}
                />
                <TextField
                    type={"text"}
                    value={firstLinkVal}
                    onChange={setFirstLink}
                    placeholder={__("text-fields.link.placeholder")}
                    disabled={!!disabled}
                />
                <TextField
                    type={"text"}
                    value={secondLinkDescVal}
                    onChange={setSecondLinkDesc}
                    placeholder={__("text-fields.link-title.placeholder")}
                    disabled={!!disabled}
                />
                <TextField
                    type={"text"}
                    value={secondLinkVal}
                    onChange={setSecondLink}
                    placeholder={__("text-fields.link.placeholder")}
                    disabled={!!disabled}
                />
                <TextField
                    type={"text"}
                    value={thirdLinkDescVal}
                    onChange={setThirdLinkDesc}
                    placeholder={__("text-fields.link-title.placeholder")}
                    disabled={!!disabled}
                />
                <TextField
                    type={"text"}
                    value={thirdLinkVal}
                    onChange={setThirdLink}
                    placeholder={__("text-fields.link.placeholder")}
                    disabled={!!disabled}
                />
            </div>
        </div>
        <Errors errors={formErrors} />
    </div>;
};

ProfileEditForm.propTypes = {
    onChange: func,
    disabled: bool
};
export const ProfileEditFormBaseState = {
    image: null,
    firstName: "",
    lastName: "",
    birthDate: "",
    gender: "",
    office: "",
    userBio: "",
    phone: "",
    languages: [],
    linkedin: "",
    twitter: "",
    website: "",
    skills: [],
    interests: [],
    links: [],
    special_request_remaining: 0
};

export default connect(mapStateToProps, mapDispatchToProps)(ProfileEditForm);